import { useState } from "react";
import { Landmark, Loader2, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { clp } from "@/lib/api";

export const FinanciamientoModal = ({ financiador, patente = "", valor = null, extra = null, origen = "portal", onClose }) => {
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", rut: "", patente, ingreso: "",
    monto: extra?.monto ?? (valor ? Math.round(valor * 0.8) : "") });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const color = financiador?.color || "#13315C";

  const enviar = async () => {
    if (!form.nombre.trim() || !form.email.includes("@")) { toast.error("Ingresa tu nombre y un email válido."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/api/aliados/${financiador.slug}/financiamiento`, {
        nombre: form.nombre, email: form.email, telefono: form.telefono, rut: form.rut, patente: form.patente,
        monto: form.monto ? Number(form.monto) : null, valor_vehiculo: valor || null, origen,
        ingreso_mensual: form.ingreso ? Number(form.ingreso) : null,
        pie: extra?.pie ?? null, plazo_meses: extra?.plazo_meses ?? null, cuota_estimada: extra?.cuota_estimada ?? null,
      });
      setDone(data.mensaje);
    } catch (e) { toast.error(e?.response?.data?.detail || "No pudimos enviar la solicitud."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-testid="financiamiento-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ background: color }}>
              {financiador.logo_url ? <img src={financiador.logo_url} alt="" className="h-6 w-auto" /> : <Landmark size={18} />}
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-wide font-bold" style={{ color }}>Aliado financiero</p>
              <h3 className="font-display font-bold text-xl text-[#13315C]">{financiador.texto}</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-[#13315C]" data-testid="financiamiento-close"><X size={20} /></button>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="financiamiento-ok">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
            <p className="mt-3 text-sm text-slate-700">{done}</p>
            <Button onClick={onClose} className="mt-4 bg-[#13315C] text-white" data-testid="financiamiento-ok-close">Cerrar</Button>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-500 mb-4">
              Déjanos tus datos y {financiador.nombre} evaluará tu crédito automotriz para esta compra. Sin costo ni compromiso.
            </p>
            {extra?.plazo_meses && (
              <div className="rounded-lg px-3 py-2 mb-3 text-xs flex flex-wrap gap-x-4 gap-y-1" style={{ background: `${color}12`, color }} data-testid="fin-resumen">
                <span>Pie <b>{clp(extra.pie)}</b></span><span>Plazo <b>{extra.plazo_meses} meses</b></span><span>Cuota est. <b>{clp(extra.cuota_estimada)}/mes</b></span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={form.nombre} onChange={set("nombre")} placeholder="Nombre completo" className="h-10 text-sm sm:col-span-2" data-testid="fin-nombre" />
              <Input value={form.email} onChange={set("email")} placeholder="Email" type="email" className="h-10 text-sm" data-testid="fin-email" />
              <Input value={form.telefono} onChange={set("telefono")} placeholder="Teléfono (+56 9…)" className="h-10 text-sm" data-testid="fin-telefono" />
              <Input value={form.rut} onChange={set("rut")} placeholder="RUT (opcional)" className="h-10 text-sm" data-testid="fin-rut" />
              <Input value={form.patente} onChange={set("patente")} placeholder="Patente" className="h-10 text-sm uppercase" data-testid="fin-patente" />
              <Input value={form.monto} onChange={set("monto")} placeholder="Monto a financiar (CLP)" type="number" className="h-10 text-sm" data-testid="fin-monto" />
              <Input value={form.ingreso} onChange={set("ingreso")} placeholder="Ingreso mensual líquido (opcional)" type="number" className="h-10 text-sm" data-testid="fin-ingreso" />
            </div>
            <Button onClick={enviar} disabled={busy} className="w-full mt-4 h-11 text-white font-semibold" style={{ background: color }} data-testid="fin-enviar">
              {busy ? <Loader2 size={16} className="animate-spin" /> : `Solicitar evaluación con ${financiador.nombre}`}
            </Button>
            <p className="text-[10px] text-slate-400 mt-2">
              Tus datos se comparten únicamente con {financiador.nombre} para evaluar el financiamiento (Ley 19.628 / 21.719).
            </p>
          </>
        )}
      </div>
    </div>
  );
};
